import React from 'react';
const { useState } = React;
import { DIAGNOSTIC_FORMS, APTITUDES, ENERGY_OPTIONS, DIAGNOSTIC_CONFIG, t } from '../../diagnosticConfig.js';
import { cardSt, labelSt, mutedSt, badgeSt } from './shared.jsx';

// Read-only answers of one person, to prepare the 1:1. Texts come from the form
// definitions, so labels match what the person saw.

const STATUS = { draft: 'En curso', submitted: 'Enviado' };
const optLabel = (q, key) => t(q.options?.find(o => o.key === key)?.label) || key;
const withOtherOf = (q, k, answers) => {
  const other = answers[k === 'other' ? `${q.key}Other` : `${q.key}_${k}Other`];
  return optLabel(q, k) + (other ? ` (${other})` : '');
};

function formatValue(q, value, answers, questions) {
  const withOther = (k) => withOtherOf(q, k, answers);
  switch (q.type) {
    case 'single': return withOther(value);
    case 'multi': return value.map(withOther).join(' · ');
    case 'rank':
    case 'pick': return value.map((k, i) => `${i + 1}. ${optLabel(q, k)}`).join('   ');
    case 'matrix': return q.rows.filter(r => value[r.key]).map(r => `${t(r.label)}: ${optLabel(q, value[r.key])}`).join(' · ');
    case 'perSelected': {
      const source = questions.find(x => x.key === q.source);
      return Object.entries(value).map(([k, v]) => `${source ? withOtherOf(source, k, answers) : k}: ${optLabel(q, v)}`).join(' · ');
    }
    default: return String(value);
  }
}

function QA({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ ...mutedSt, fontSize: 11, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{children}</div>
    </div>
  );
}

function Questions({ questions, answers }) {
  const shown = questions.filter(q => answers[q.key] !== undefined && answers[q.key] !== null && answers[q.key] !== '');
  if (!shown.length) return <div style={mutedSt}>Sin respuestas.</div>;
  return shown.map(q => <QA key={q.key} label={t(q.label)}>{formatValue(q, answers[q.key], answers, questions)}</QA>);
}

function Aptitudes({ answers, manager }) {
  const levels = answers.levels || {};
  const keys = APTITUDES.filter(a => levels[a.key] !== undefined);
  if (!keys.length) return null;
  const energy = (k) => ENERGY_OPTIONS.find(e => e.key === answers.energy?.[k]);
  return (
    <div style={{ marginBottom: 10 }}>
      {keys.map(a => {
        const lv = levels[a.key];
        const text = lv === 'unknown' ? t(DIAGNOSTIC_CONFIG.managerUnknownOption.label)
          : `${lv} · ${t((manager ? a.manager : a.selfAssessment)?.[lv])}`;
        const en = energy(a.key);
        return (
          <QA key={a.key} label={t(a.label)}>
            {text}{en ? `   Energía: ${en.symbol} ${t(en.label)}` : ''}
            {answers.examples?.[a.key] && <div style={{ ...mutedSt, marginTop: 2 }}>Ejemplo: {answers.examples[a.key]}</div>}
          </QA>
        );
      })}
    </div>
  );
}

function Tools({ section, answers }) {
  const tools = answers.tools || {};
  return Object.entries(tools).map(([cat, item]) => (
    <div key={cat} style={{ borderLeft: '2px solid var(--globant-border)', paddingLeft: 10, marginBottom: 10 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{t(section.categories.find(c => c.key === cat)?.label) || cat}</div>
      <Questions questions={section.itemQuestions} answers={item} />
    </div>
  ));
}

/** One stored form (or part of it): { status, answers } keyed by section. */
export function FormAnswers({ formKey, record, sections, title, manager = false }) {
  const [open, setOpen] = useState(false);
  if (!record) return null;
  const defs = DIAGNOSTIC_FORMS[formKey].sections.filter(s => (sections ? sections.includes(s.key) : true) && s.store !== 'motivation');
  return (
    <div style={cardSt}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <div style={labelSt}>{title}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={badgeSt(record.status === 'submitted')}>{STATUS[record.status] || record.status}</span>
          <span style={mutedSt}>{open ? '▲' : '▼'}</span>
        </div>
      </div>
      {open && defs.map(s => {
        const a = record.answers[s.key];
        if (!a) return null;
        return (
          <div key={s.key} style={{ marginTop: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{t(s.title)}</div>
            {(s.kind === 'aptitudes' || s.kind === 'person') && <Aptitudes answers={a} manager={manager} />}
            {s.kind === 'tools' ? <Tools section={s} answers={a} /> : <Questions questions={s.questions || []} answers={a} />}
          </div>
        );
      })}
    </div>
  );
}

/** Parte 3 of this person only: confidential, to prepare the 1:1. */
export function ConfidentialMotivation({ block }) {
  const [open, setOpen] = useState(false);
  if (!block) return null;
  const section = DIAGNOSTIC_FORMS.self.sections.find(s => s.key === 'motivation');
  return (
    <div style={{ ...cardSt, borderColor: 'rgba(239,68,68,0.45)', background: 'rgba(239,68,68,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div>
          <div style={{ ...labelSt, color: '#B4362C' }}>🔒 {block.note}</div>
          <div style={mutedSt}>Parte 3 de la autoevaluación. No entra en ningún agregado ni llega al founder.</div>
        </div>
        <button className="action-btn" style={{ fontSize: 11 }} onClick={() => setOpen(o => !o)}>{open ? 'Ocultar' : 'Mostrar'}</button>
      </div>
      {open && <div style={{ marginTop: 12 }}><Questions questions={section.questions} answers={block.motivation} /></div>}
    </div>
  );
}
